import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export interface PaginationModel {
  page: number;
  pageSize: number;
}

export interface CustomServerPaginatedTableProps<
  TData extends { id?: string | number },
> {
  rows: TData[];
  cols: ColumnDef<TData>[];
  rowCount: number;
  paginationModel: PaginationModel;
  setPaginationModel: (newModel: PaginationModel) => void;
  handleOnRowClick?: (row: TData) => void;
  isLoading: boolean;
  overlayText: string;
  getCustomRowClass?: (row: TData) => string;
  checkboxSelection?: boolean;
  onSelectionModelChange?: (selectedIds: string[]) => void;
  isRowSelectable?: (row: TData) => boolean;
  hideFooter?: boolean;
  getRowId?: (row: TData) => string;
  customStyle?: string;
  disableColumnMenu?: boolean;
  disableColumnSelector?: boolean;
  enableSorting?: boolean;
  sortIndicator?: {
    columnId: string;
    direction: "asc" | "desc";
    description?: string;
  };
}

const CustomServerPaginatedTable = <TData extends { id?: string | number }>({
  rows,
  cols,
  rowCount,
  paginationModel,
  setPaginationModel,
  handleOnRowClick,
  isLoading,
  overlayText,
  getCustomRowClass,
  checkboxSelection,
  onSelectionModelChange,
  isRowSelectable,
  hideFooter = false,
  getRowId,
  enableSorting = true,
  sortIndicator,
}: CustomServerPaginatedTableProps<TData>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo(() => {
    if (!checkboxSelection) return cols;

    const selectColumn: ColumnDef<TData, any> = {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => {
        const selectable = isRowSelectable
          ? isRowSelectable(row.original)
          : true;
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            disabled={!selectable}
            aria-label="Select row"
          />
        );
      },
      size: 40,
      enableSorting: false,
    };

    return [selectColumn, ...cols];
  }, [cols, checkboxSelection, isRowSelectable]);

  const pageCount = Math.ceil(rowCount / paginationModel.pageSize);

  const table = useReactTable({
    data: rows,
    columns,
    pageCount: rowCount === -1 ? -1 : pageCount,
    state: {
      pagination: {
        pageIndex: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
      rowSelection,
      sorting,
    },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function"
          ? updater({
              pageIndex: paginationModel.page,
              pageSize: paginationModel.pageSize,
            })
          : updater;
      setPaginationModel({ page: next.pageIndex, pageSize: next.pageSize });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    enableSorting,
    enableSortingRemoval: true,
    getRowId: getRowId
      ? (row) => getRowId(row)
      : (row) => String(row.id ?? ""),
    enableRowSelection: isRowSelectable
      ? (row) => isRowSelectable(row.original)
      : true,
  });

  useEffect(() => {
    if (!onSelectionModelChange) return;
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => rowSelection[key],
    );
    const ids = selectedIds.map((idx) => {
      const row = table.getRow(idx);
      return row?.id ?? idx;
    });
    onSelectionModelChange(ids);
  }, [rowSelection]);

  return (
    <div className="w-full space-y-2">
      <div className="rounded-md border">
        <Table aria-busy={isLoading}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const indicatedSort =
                    sortIndicator?.columnId === header.column.id
                      ? sortIndicator
                      : undefined;
                  const sortDir =
                    indicatedSort?.direction ?? header.column.getIsSorted();
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      );
                  return (
                    <TableHead
                      key={header.id}
                      aria-sort={
                        sortDir === "asc"
                          ? "ascending"
                          : sortDir === "desc"
                            ? "descending"
                            : undefined
                      }
                      title={indicatedSort?.description}
                      style={{
                        width:
                          header.getSize() !== 150
                            ? header.getSize()
                            : undefined,
                      }}
                    >
                      {canSort && !header.isPlaceholder ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex items-center gap-1 select-none hover:text-foreground"
                        >
                          {content}
                          {sortDir === "asc" ? (
                            <ArrowUp className="size-3" />
                          ) : sortDir === "desc" ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-50" />
                          )}
                        </button>
                      ) : sortDir && !header.isPlaceholder ? (
                        <span className="flex items-center gap-1">
                          {content}
                          {sortDir === "asc" ? (
                            <ArrowUp className="size-3" aria-hidden="true" />
                          ) : (
                            <ArrowDown className="size-3" aria-hidden="true" />
                          )}
                        </span>
                      ) : (
                        content
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(columns.length, 1)}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Spinner />
                    <span className="text-muted-foreground">Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {overlayText}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={
                    getCustomRowClass
                      ? getCustomRowClass(row.original)
                      : undefined
                  }
                  onClick={
                    handleOnRowClick
                      ? () => handleOnRowClick(row.original)
                      : undefined
                  }
                  style={handleOnRowClick ? { cursor: "pointer" } : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {!hideFooter && (
        <div className="flex items-center justify-between px-2 py-2">
          {checkboxSelection && (
            <div className="flex-1 text-xs text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} row(s) selected.
            </div>
          )}
          <div className="flex items-center gap-4 ml-auto">
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium">Rows per page</p>
              <Select
                value={`${paginationModel.pageSize}`}
                onValueChange={(value) => {
                  setPaginationModel({
                    page: 0,
                    pageSize: Number(value),
                  });
                }}
              >
                <SelectTrigger className="h-8 w-17.5">
                  <SelectValue placeholder={paginationModel.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 25, 50, 100].map((size) => (
                    <SelectItem key={size} value={`${size}`}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs">
                Page {paginationModel.page + 1} of{" "}
                {pageCount > 0 ? pageCount : "..."}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() =>
                  setPaginationModel({ ...paginationModel, page: 0 })
                }
                disabled={paginationModel.page === 0}
              >
                <ChevronsLeft className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() =>
                  setPaginationModel({
                    ...paginationModel,
                    page: paginationModel.page - 1,
                  })
                }
                disabled={paginationModel.page === 0}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() =>
                  setPaginationModel({
                    ...paginationModel,
                    page: paginationModel.page + 1,
                  })
                }
                disabled={
                  pageCount > 0 && paginationModel.page >= pageCount - 1
                }
              >
                <ChevronRight className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() =>
                  setPaginationModel({
                    ...paginationModel,
                    page: pageCount - 1,
                  })
                }
                disabled={
                  pageCount > 0 && paginationModel.page >= pageCount - 1
                }
              >
                <ChevronsRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomServerPaginatedTable;
